;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import {
	defaultShouldDehydrateQuery,
	environmentManager,
	QueryCache,
	QueryClient,
	type QueryKey,
	type QueryMeta,
} from "@tanstack/react-query";
import { ApiRequestError } from "@/lib/api/api-request-error";
import { isExpectedClientFailure } from "@/lib/observability/is-expected-client-failure";
import { logger } from "@/lib/services/logger";

type ReportableQuery = { meta?: QueryMeta; queryKey: QueryKey };

// Some families lead with a tenant id (visualization keys start with
// `workspaceId`); ids contain digits, family names do not.
const QUERY_FAMILY_SEGMENT_PATTERN = /^[a-z][a-z_-]*$/i;

export function getQueryKeyRoot(query: ReportableQuery): string {
	const familySegment = query.queryKey.find(
		(segment): segment is string =>
			typeof segment === "string" && QUERY_FAMILY_SEGMENT_PATTERN.test(segment),
	);
	return familySegment ?? "unknown";
}

/**
 * Mutations are not reported here: their hooks already call
 * `logger.captureUnexpected` with richer context in `onError`.
 * Queries can opt out with `meta: { reportErrors: false }`.
 */
export function reportQueryError({
	error,
	query,
}: {
	error: unknown;
	query: ReportableQuery;
}) {
	if (query.meta?.reportErrors === false) {
		return;
	}

	const queryKeyRoot = getQueryKeyRoot(query);
	const failureKind =
		error instanceof ApiRequestError
			? String(error.status)
			: error instanceof Error
				? error.name
				: "unknown";

	logger.captureUnexpected("Query failed", error, {
		feature: "react-query",
		fingerprint: ["{{ default }}", "react-query", queryKeyRoot, failureKind],
		operation: queryKeyRoot,
		tags: { queryKeyRoot },
	});
}

function makeQueryClient() {
	return new QueryClient({
		defaultOptions: {
			dehydrate: {
				// include pending queries in dehydration
				shouldDehydrateQuery: (query) =>
					defaultShouldDehydrateQuery(query) ||
					query.state.status === "pending",
			},
			queries: {
				gcTime: 5 * 60 * 1000,
				maxPages: 5,
				networkMode: "online",
				// Opt in per query; avoid blanket polling (sync-backed data is hourly at most).
				refetchInterval: false,
				refetchOnWindowFocus: false, // Reduce unnecessary refetches
				// 4xx will not change on retry; server errors retry up to 2 times.
				retry: (failureCount, error) =>
					!isExpectedClientFailure(error) && failureCount < 2,
				staleTime: 5 * 60 * 1000,
			},
		},
		queryCache: new QueryCache({
			onError: (error, query) => reportQueryError({ error, query }),
		}),
	});
}

let browserQueryClient: QueryClient | undefined;

export function getQueryClient() {
	if (environmentManager.isServer.call(environmentManager)) {
		// Server: always make a new query client
		return makeQueryClient();
	}
	// Browser: make a new query client if we don't already have one
	// This is very important, so we don't re-make a new client if React
	// suspends during the initial render. This may not be needed if we
	// have a suspense boundary BELOW the creation of the query client
	if (!browserQueryClient) browserQueryClient = makeQueryClient();
	return browserQueryClient;
}
